<template>
  <section class="home-feed">
    <div class="home-feed__header">
      <h2 class="home-feed__title">Your feed</h2>
      <p class="home-feed__subtitle">Insights and analysis from our experts</p>
    </div>
    <div class="home-feed__tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="home-feed__tab"
        :class="{ 'home-feed__tab--active': activeTab === tab.key }"
        @click="setTab(tab.key)"
      >
        {{ tab.label }}
      </button>
      <button class="home-feed__filter-btn" @click="$emit('open-filters')">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 3h14M4 8h8M6 13h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        Filter
      </button>
    </div>
    <div class="home-feed__list">
      <article
        v-for="article in visibleArticles"
        :key="article.nid || article.url || article.title"
        class="feed-card"
      >
        <div class="feed-card__meta">
          <span v-if="article.contentType" class="feed-card__type">{{ article.contentType }}</span>
          <span v-if="article.author" class="feed-card__dot">&middot;</span>
          <span v-if="article.author" class="feed-card__author">{{ article.author }}</span>
          <span v-if="article.publishedDate" class="feed-card__dot">&middot;</span>
          <span v-if="article.publishedDate" class="feed-card__date">{{ article.publishedDate }}</span>
        </div>
        <nuxt-link :to="articleLink(article)" class="feed-card__title-link">
          <h3 class="feed-card__title">{{ decodeText(article.title) }}</h3>
        </nuxt-link>
        <p v-if="article.summary" class="feed-card__summary">{{ decodeText(article.summary) }}</p>
        <div v-if="article.tags && article.tags.length" class="feed-card__tags">
          <nuxt-link
            v-for="tag in article.tags.slice(0, 3)"
            :key="tag.tid || tag.title"
            :to="tag.url || '#'"
            class="feed-card__tag"
          >
            {{ tag.title || tag.name || tag }}
          </nuxt-link>
          <span v-if="article.tags.length > 3" class="feed-card__tag feed-card__tag--more">
            +{{ article.tags.length - 3 }}
          </span>
        </div>
      </article>
      <div v-if="!filteredArticles.length && !loading" class="home-feed__empty">
        No articles found for this filter.
      </div>
    </div>
    <div v-if="loading" class="home-feed__loader">
      <div class="home-feed__spinner" />
      <span>Loading more articles...</span>
    </div>
    <div v-else-if="canShowMore" class="home-feed__load-more">
      <button class="home-feed__load-more-btn" @click="handleLoadMore">
        Load More
      </button>
    </div>
  </section>
</template>

<script>
export default {
  name: "HomeFeed",
  props: {
    articles: {
      type: Array,
      default: () => [],
    },
    tabs: {
      type: Array,
      default: () => [
        { key: "all", label: "All articles" },
        { key: "analysis", label: "Analysis" },
        { key: "insights", label: "Insights" },
        { key: "horizon_scanning", label: "Horizon Scanning" },
      ],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasMore: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeTab: "all",
      displayCount: 15,
    };
  },
  computed: {
    filteredArticles() {
      if (this.activeTab === "all") {
        return this.articles;
      }
      return this.articles.filter(
        (a) => a.contentType && a.contentType.toLowerCase().replace(/\s+/g, "_") === this.activeTab
      );
    },
    visibleArticles() {
      return this.filteredArticles.slice(0, this.displayCount);
    },
    canShowMore() {
      return this.filteredArticles.length > this.displayCount || this.hasMore;
    },
  },
  methods: {
    setTab(key) {
      this.activeTab = key;
      this.displayCount = 15;
      this.$emit("tab-change", key);
    },
    articleLink(article) {
      const url = this.articleUrl(article);
      if (url === "#") return url;
      const separator = url.includes("?") ? "&" : "?";
      return `${url}${separator}ref=feed`;
    },
    articleUrl(article) {
      // Featured items use `url`, insights/analysis use `relativeLink`
      if (article.url && article.url.length > 0) {
        return article.url;
      }
      if (article.relativeLink && article.relativeLink.length > 0) {
        return article.relativeLink;
      }
      // HS list items have empty url — build from id and title
      if (article.id) {
        const slug = (article.title || "")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
        return `/regulatory_update/${article.id}/${slug}`;
      }
      return "#";
    },
    decodeText(text) {
      if (!text) return "";
      // Strip HTML tags then decode entities
      const stripped = text.replace(/<[^>]*>/g, "");
      if (typeof document !== "undefined") {
        const el = document.createElement("textarea");
        el.innerHTML = stripped;
        return el.value;
      }
      // SSR fallback — decode common entities
      return stripped
        .replace(/&#039;/g, "'")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, "\"")
        .replace(/&apos;/g, "'")
        .replace(/&#(\d+);/g, (m, n) => String.fromCharCode(n));
    },
    handleLoadMore() {
      // Show more from local data first
      if (this.filteredArticles.length > this.displayCount) {
        this.displayCount += 15;
      }
      // Also fetch more from API if needed
      if (!this.loading && this.hasMore) {
        this.$emit("load-more");
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import "../../assets/styles/helpers/variables";
@import "../../assets/styles/helpers/mixin";

.home-feed {
  &__header {
    margin-bottom: 20px;
  }

  &__title {
    font-size: 2.2rem;
    font-weight: 700;
    color: $text;
    margin: 0 0 2px;
    line-height: 1.3;
  }

  &__subtitle {
    font-size: 1.3rem;
    color: $light_gray;
    margin: 0;
  }

  &__tabs {
    display: flex;
    align-items: stretch;
    border-bottom: 2px solid #ecedf0;
    margin-bottom: 0;
    position: relative;
  }

  &__tab {
    background: none;
    border: none;
    padding: 12px 16px;
    font-size: 1.3rem;
    font-weight: 600;
    color: $light_gray;
    cursor: pointer;
    white-space: nowrap;
    position: relative;
    transition: color 0.15s ease;

    @include below($md) {
      padding: 10px 12px;
      font-size: 1.2rem;
    }

    &::after {
      content: "";
      position: absolute;
      bottom: -2px;
      left: 12px;
      right: 12px;
      height: 0;
      background: $corporateBlue;
      transition: height 0.15s ease;
    }

    &:hover {
      color: $text;

      &::after {
        height: 2px;
        background: rgba($corporateBlue, 0.3);
      }
    }

    &--active {
      color: $text;

      &::after {
        height: 2px;
        background: $corporateBlue;
      }
    }
  }

  &__filter-btn {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: none;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 5px 12px;
    font-size: 1.2rem;
    font-weight: 600;
    color: $light_gray;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, color 0.15s ease;
    margin-bottom: 8px;
    flex-shrink: 0;

    &:hover {
      background: #f5f6f8;
      color: $text;
    }

    svg {
      flex-shrink: 0;
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
  }

  &__empty {
    padding: 48px 24px;
    text-align: center;
    color: $light_gray;
    font-size: 1.4rem;
  }

  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 24px 0;
    color: $light_gray;
    font-size: 1.3rem;
  }

  &__spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #ecedf0;
    border-top-color: $corporateBlue;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  &__load-more {
    display: flex;
    justify-content: center;
    padding: 24px 0;
  }

  &__load-more-btn {
    padding: 10px 32px;
    font-size: 1.3rem;
    font-weight: 600;
    color: $corporateBlue;
    background: none;
    border: 1px solid $corporateBlue;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover {
      background: $corporateBlue;
      color: #fff;
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.feed-card {
  padding: 20px 0;
  border-bottom: 1px solid #ecedf0;

  &:first-child {
    padding-top: 16px;
  }

  &:last-child {
    border-bottom: none;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
    font-size: 1.2rem;
    color: $light_gray;
    flex-wrap: wrap;
  }

  &__type {
    display: inline-block;
    padding: 1px 8px;
    background: rgba($corporateBlue, 0.06);
    color: $corporateBlue;
    border-radius: 3px;
    font-weight: 600;
    font-size: 1.1rem;
  }

  &__dot {
    color: #ccc;
    font-size: 1rem;
  }

  &__title-link {
    text-decoration: none;
    display: block;
  }

  &__title {
    font-size: 1.6rem;
    font-weight: 700;
    color: $text;
    line-height: 1.4;
    margin: 0 0 4px;
    transition: color 0.15s ease;

    .feed-card__title-link:hover & {
      color: $accentBlue;
    }
  }

  &__summary {
    font-size: 1.35rem;
    line-height: 1.55;
    color: $light_gray;
    margin: 0 0 10px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__tags {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__tag {
    display: inline-block;
    padding: 2px 10px;
    border: 1px solid $tagBorder;
    border-radius: 3px;
    font-size: 1.1rem;
    color: $tagColor;
    white-space: nowrap;
    text-decoration: none;
    transition: background 0.12s ease, border-color 0.12s ease;

    &:hover {
      background: #f5f6f8;
      border-color: $light_gray;
    }

    &--more {
      background: #f5f6f8;
      border-color: transparent;
      color: $light_gray;
      cursor: default;

      &:hover {
        background: #f5f6f8;
        border-color: transparent;
      }
    }
  }
}
</style>
